import React from 'react';
import { Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import * as modelViewer from '@google/model-viewer';
import { Annotation } from '@/types/Annotation.model';
import { IProduct } from '@/types/Product.model';
import {
  presentationSettingsToAttrs,
  presentationSettingsToViewerStyle,
  type ModelViewerPresentationSettings,
} from '@/constants/modelViewerPresentation';
import { MODEL_VIEWER_RELAXED_ZOOM_ATTRS } from '@/utils/annotationConstants';
import {
  AnnotationNavigator,
  AnnotationNavigatorRef,
  AnnotationImageModal,
  AnnotationProductModal,
  AnnotationHotspot,
  AnnotationInfoBox,
  useAnnotationBarPortalTargetStyle,
} from '@/components/Annotations';
import { ModelViewerPresentationPanel } from '@/components/ModelViewer/ModelViewerPresentationPanel';
import { ModelViewerResetZoomButton } from '@/components/ModelViewer/ModelViewerResetZoomButton';
import { ModelViewerScreenshotButton } from '@/components/ModelViewer/ModelViewerScreenshotButton';
import { ModelViewerHelpButton } from '@/components/ModelViewer/ModelViewerHelpButton';
import {
  ModelViewerHelpOverlay,
  type ModelViewerHelpFeatures,
} from '@/components/ModelViewer/ModelViewerHelpOverlay';
import { resetModelViewerCamera } from '@/utils/modelViewerZoom';

const EDIT_MODEL_VIEWER_ANNOTATION_BAR_ID = 'edit-model-viewer-annotation-bar';

export interface ModelViewerStageProps {
  modelUrl: string;
  modelViewerRef: React.RefObject<modelViewer.ModelViewerElement>;
  presentationSettings: ModelViewerPresentationSettings;
  onPresentationChange: (settings: ModelViewerPresentationSettings) => void;
  isAnnotationMode: boolean;
  annotations: Annotation[];
  annotationsVisible: boolean;
  onAnnotationsVisibleChange: (visible: boolean) => void;
  onActiveAnnotationChange: (annotation: Annotation | null) => void;
  activeAnnotation: Annotation | null;
  annotationPosition: { x: number; y: number } | null;
  handleAnnotationClick: (annotation: Annotation, event?: React.MouseEvent) => void;
  annotationNavigatorRef: React.RefObject<AnnotationNavigatorRef>;
  descriptionBoxRef: React.RefObject<HTMLDivElement>;
  imageModalOpened: boolean;
  onImageModalClose: () => void;
  productModalOpened: boolean;
  onProductModalClose: () => void;
  selectedProduct: IProduct | null;
  onProductClick: (product: IProduct) => void;
  onImageClick: () => void;
  initialCameraOrbit: string;
  initialCameraTarget: string;
  initialCameraFieldOfView: string;
  chromeColor?: string;
  helpOpened?: boolean;
  onHelpOpenedChange?: (opened: boolean) => void;
}

export function ModelViewerStage({
  modelUrl,
  modelViewerRef,
  presentationSettings,
  onPresentationChange,
  isAnnotationMode,
  annotations,
  annotationsVisible,
  onAnnotationsVisibleChange,
  onActiveAnnotationChange,
  activeAnnotation,
  annotationPosition,
  handleAnnotationClick,
  annotationNavigatorRef,
  descriptionBoxRef,
  imageModalOpened,
  onImageModalClose,
  productModalOpened,
  onProductModalClose,
  selectedProduct,
  onProductClick,
  onImageClick,
  initialCameraOrbit,
  initialCameraTarget,
  initialCameraFieldOfView,
  chromeColor = '#000000',
  helpOpened = false,
  onHelpOpenedChange,
}: ModelViewerStageProps) {
  const { t } = useTranslation();
  const annotationBarPortalStyle = useAnnotationBarPortalTargetStyle({
    zIndex: 12,
    ...(isAnnotationMode ? { pointerEvents: 'none' as const } : {}),
  });
  const hasModel = Boolean(modelUrl);

  const editorHelpFeatures: ModelViewerHelpFeatures = {
    resetView: true,
    appearance: true,
    annotationEdit: true,
    shortcuts: {
      save: true,
      annotationMode: true,
      annotationStep: true,
      animationStep: true,
      resetView: true,
      help: true,
      escape: true,
    },
  };

  const handleResetCamera = () => {
    const mv = modelViewerRef.current;
    if (!mv) return;
    resetModelViewerCamera(mv, {
      cameraOrbit: initialCameraOrbit,
      cameraTarget: initialCameraTarget,
      cameraFieldOfView: initialCameraFieldOfView,
    });
  };

  return (
    <Box style={{ position: 'relative', width: '100%' }}>
      {isAnnotationMode && (
        <Box
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            zIndex: 1,
            padding: '8px 16px',
            background: chromeColor,
            color: '#fff',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {t('editProduct.annotationModeActive')}
        </Box>
      )}

      {hasModel && (
        <>
          <Box
            style={{
              position: 'absolute',
              bottom: '48px',
              right: '16px',
              zIndex: 12,
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <ModelViewerPresentationPanel
              settings={presentationSettings}
              onChange={onPresentationChange}
              modelViewerRef={modelViewerRef}
              color={chromeColor}
              inline
            />
            <AnnotationNavigator
              ref={annotationNavigatorRef}
              annotations={annotations}
              modelViewerRef={modelViewerRef}
              color={chromeColor}
              isAnnotationMode={isAnnotationMode}
              onAnnotationChange={onActiveAnnotationChange}
              onAnnotationsVisibleChange={onAnnotationsVisibleChange}
              inline
              barPortalTargetId={EDIT_MODEL_VIEWER_ANNOTATION_BAR_ID}
            />
          </Box>

          <ModelViewerResetZoomButton
            modelViewerRef={modelViewerRef}
            placement="top-right"
            cameraDefaults={{
              cameraOrbit: initialCameraOrbit,
              cameraTarget: initialCameraTarget,
              cameraFieldOfView: initialCameraFieldOfView,
            }}
          />
          <ModelViewerScreenshotButton
            modelViewerRef={modelViewerRef}
            placement="top-right"
          />

          {onHelpOpenedChange ? (
            <>
              <ModelViewerHelpButton
                onClick={() => onHelpOpenedChange(true)}
                zIndex={12}
              />
              <ModelViewerHelpOverlay
                opened={helpOpened}
                onClose={() => onHelpOpenedChange(false)}
                onResetCamera={handleResetCamera}
                features={editorHelpFeatures}
                zIndex={40}
              />
            </>
          ) : null}

          <Box id={EDIT_MODEL_VIEWER_ANNOTATION_BAR_ID} style={annotationBarPortalStyle} />
        </>
      )}

      <model-viewer
        id="viewer"
        ref={modelViewerRef}
        style={{
          display: 'block',
          width: '100%',
          minWidth: '280px',
          height: 'clamp(400px, 72vh, 900px)',
          marginBottom: '10px',
          ...presentationSettingsToViewerStyle(presentationSettings),
          ...(isAnnotationMode && { cursor: 'crosshair' }),
        }}
        src={modelUrl}
        ar
        {...presentationSettingsToAttrs(presentationSettings)}
        camera-controls
        disable-tap={isAnnotationMode || undefined}
        {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
        touch-action="pan-y"
        autoPlay
      >
        {annotationsVisible &&
          annotations.map((annotation) => (
            <AnnotationHotspot
              key={annotation.id}
              annotation={annotation}
              onClick={handleAnnotationClick}
            />
          ))}
      </model-viewer>

      {activeAnnotation && annotationPosition && (
        <AnnotationInfoBox
          activeAnnotation={activeAnnotation}
          annotationPosition={annotationPosition}
          descriptionBoxRef={descriptionBoxRef}
          backgroundColor={chromeColor}
          onProductClick={onProductClick}
          onImageClick={onImageClick}
        />
      )}

      <AnnotationImageModal
        opened={imageModalOpened}
        onClose={onImageModalClose}
        annotation={activeAnnotation}
      />

      <AnnotationProductModal
        opened={productModalOpened}
        onClose={onProductModalClose}
        product={selectedProduct}
      />
    </Box>
  );
}
